import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import {
  ArrowRight, User, Building2, Home, Mail, Phone, MapPin, Hash, IndianRupee,
  Flame, Sparkles, Camera, ScanLine, Loader2, CheckCircle2, Sun, Gauge,
} from "lucide-react";

const TYPES = [
  { value: "residential", label: "Home", Icon: Home, testid: "home" },
  { value: "commercial", label: "Business", Icon: Building2, testid: "business" },
  { value: "housing_society", label: "Society", Icon: User, testid: "society" },
];

const ROOF_TYPES = [
  { value: "rcc", label: "RCC" },
  { value: "tin_shed", label: "Tin Shed" },
  { value: "tiled", label: "Tiled" },
  { value: "other", label: "Other" },
];

const inputCls = "w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#1B5E20] focus:ring-2 focus:ring-emerald-600/15 rounded-xl pl-10 pr-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-all duration-200 outline-none";

export default function ScNewLead() {
  const nav = useNavigate();
  const billInputRef = useRef(null);
  const roofInputRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", pin_code: "",
    customer_type: "residential", establishment_name: "",
    avg_power_consumption: "", category: "warm", notes: "",
    roof_type: "", consumer_number: "", sanctioned_load_kw: "",
  });
  const [billPhotoPath, setBillPhotoPath] = useState(null);
  const [roofPhotoPath, setRoofPhotoPath] = useState(null);
  const [roofGeo, setRoofGeo] = useState(null);
  const [scanningBill, setScanningBill] = useState(false);
  const [uploadingRoof, setUploadingRoof] = useState(false);
  const [ocrApplied, setOcrApplied] = useState(false);
  const [saving, setSaving] = useState(false);
  const isOrg = form.customer_type === "commercial" || form.customer_type === "housing_society";

  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onBillSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanningBill(true);
    setOcrApplied(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sc/ocr-bill", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBillPhotoPath(data.path);
      const ex = data.extracted || {};
      if (ex._error) {
        toast.info("Bill photo saved — auto-read didn't work, please fill fields manually");
      } else {
        setForm((f) => ({
          ...f,
          name: ex.name || f.name,
          phone: ex.phone || f.phone,
          pin_code: ex.pin_code ? String(ex.pin_code).replace(/\D/g, "").slice(0, 6) : f.pin_code,
          consumer_number: ex.consumer_number || f.consumer_number,
          sanctioned_load_kw: ex.sanctioned_load_kw ?? f.sanctioned_load_kw,
        }));
        setOcrApplied(true);
        toast.success("Bill scanned — fields auto-filled, please verify");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setScanningBill(false);
    }
  }

  function onRoofSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRoof(true);
    const doUpload = async (lat, lng) => {
      try {
        const fd = new FormData();
        fd.append("kind", "rooftop");
        if (lat != null) fd.append("lat", lat);
        if (lng != null) fd.append("lng", lng);
        fd.append("file", file);
        const { data } = await api.post("/sc/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRoofPhotoPath(data.path);
        if (lat != null) setRoofGeo({ lat, lng });
        toast.success("Rooftop photo added" + (lat != null ? " (location tagged)" : ""));
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setUploadingRoof(false);
      }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => doUpload(pos.coords.latitude, pos.coords.longitude),
        () => doUpload(null, null),
        { timeout: 4000 },
      );
    } else {
      doUpload(null, null);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name & phone required"); return; }
    if (!/^\d{6}$/.test(form.pin_code)) { toast.error("A valid 6-digit PIN code is required"); return; }
    if (isOrg && !form.establishment_name.trim()) { toast.error("Establishment name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        avg_power_consumption: Number(form.avg_power_consumption) || 0,
        sanctioned_load_kw: form.sanctioned_load_kw !== "" ? Number(form.sanctioned_load_kw) : null,
        roof_type: form.roof_type || null,
        consumer_number: form.consumer_number.trim() || null,
        email: form.email.trim() || null,
        establishment_name: isOrg ? form.establishment_name.trim() : null,
        bill_photo_path: billPhotoPath,
        rooftop_photo_path: roofPhotoPath,
        rooftop_lat: roofGeo?.lat ?? null,
        rooftop_lng: roofGeo?.lng ?? null,
      };
      const { data } = await api.post("/sc/leads", payload);
      toast.success(`Lead submitted · ${data.customer_name} · you're on your way to a payout!`);
      nav("/sc/leads");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="sc-new-lead" className="pb-6">
      <div
        data-testid="sc-new-lead-hero"
        className="relative overflow-hidden rounded-2xl p-5 mb-5 bg-gradient-to-br from-[#1B5E20] to-[#134A18] shadow-[0_4px_20px_rgba(27,94,32,0.25)]"
      >
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-white text-[#1B5E20] mb-2.5">
          <Sparkles className="w-3.5 h-3.5" /> Refer a lead
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-white">Earn on installation.</h1>
        <p className="text-xs font-medium text-emerald-100/90 leading-relaxed mt-1">We'll keep you updated on every step.</p>
        <div className="text-xs font-semibold text-amber-100 flex items-center gap-1.5 mt-2 bg-black/15 px-3 py-1.5 rounded-lg w-fit">
          <Flame className="w-3.5 h-3.5 text-amber-300" /> EPC referrals only — Home, Business & Housing Society
        </div>
      </div>

      {/* Bill scan CTA */}
      <input ref={billInputRef} type="file" accept="image/*" capture="environment" onChange={onBillSelected} className="hidden" data-testid="sc-bill-file-input" />
      <button
        type="button"
        onClick={() => billInputRef.current?.click()}
        disabled={scanningBill}
        data-testid="sc-scan-bill-btn"
        className="w-full mb-4 flex items-center gap-3 bg-white border-2 border-dashed border-emerald-300 rounded-xl p-4 text-left hover:border-[#1B5E20] transition-colors disabled:opacity-60 shadow-[0_2px_8px_rgba(15,23,42,0.04)]"
      >
        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
          {scanningBill ? <Loader2 className="w-5 h-5 text-[#1B5E20] animate-spin" /> : <ScanLine className="w-5 h-5 text-[#1B5E20]" />}
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-900">{scanningBill ? "Reading bill…" : "Scan electricity bill"}</div>
          <div className="text-[11px] text-slate-500">{ocrApplied ? "Applied — fields auto-filled below" : "Snap a photo, we'll auto-fill the form"}</div>
        </div>
        {ocrApplied && <CheckCircle2 className="w-5 h-5 text-[#1B5E20] flex-shrink-0" />}
      </button>

      <form onSubmit={submit} className="space-y-4" data-testid="sc-new-lead-form">
        {/* Property type */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Property Type</div>
          <div className="grid grid-cols-3 gap-2.5">
            {TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setField("customer_type", t.value)}
                data-testid={`property-type-${t.testid}`}
                className={"py-3 text-center text-[10px] font-semibold uppercase tracking-widest rounded-xl transition-all active:scale-95 flex flex-col items-center gap-1.5 border " +
                  (form.customer_type === t.value
                    ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-sm"
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-300")}
              >
                <t.Icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {isOrg && (
          <div className="relative">
            <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={form.establishment_name}
              onChange={(e) => setField("establishment_name", e.target.value)}
              placeholder={form.customer_type === "housing_society" ? "Society name" : "Company name"}
              data-testid="sc-new-lead-establishment"
              className={inputCls}
            />
          </div>
        )}

        <div className="relative">
          <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder={isOrg ? "Contact person" : "Customer name"}
            required
            data-testid="lead-name-input"
            className={inputCls}
          />
        </div>
        <div className="relative">
          <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            placeholder="Phone (+91-9XXXXXXXXX)"
            required
            data-testid="lead-phone-input"
            className={inputCls + " font-mono"}
          />
        </div>
        <div className="relative">
          <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="Email (optional)"
            data-testid="lead-email-input"
            className={inputCls}
          />
        </div>
        <div className="relative">
          <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="City + locality"
            data-testid="lead-address-input"
            className={inputCls}
          />
        </div>
        <div className="relative">
          <Hash className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            inputMode="numeric"
            maxLength={6}
            value={form.pin_code}
            onChange={(e) => setField("pin_code", e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="PIN Code *"
            required
            data-testid="lead-pincode-input"
            className={inputCls + " font-mono"}
          />
        </div>
        <div className="relative flex items-center">
          <IndianRupee className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="number"
            value={form.avg_power_consumption}
            onChange={(e) => setField("avg_power_consumption", e.target.value)}
            placeholder="Avg monthly bill (kWh)"
            data-testid="lead-bill-input"
            className={inputCls + " font-mono"}
          />
          <span className="absolute right-3 bg-emerald-50 text-[#1B5E20] text-xs font-bold px-2.5 py-1 rounded-md border border-emerald-200 pointer-events-none">kWh</span>
        </div>

        {/* Bill-derived fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <Hash className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={form.consumer_number}
              onChange={(e) => setField("consumer_number", e.target.value)}
              placeholder="Consumer no."
              data-testid="lead-consumer-number-input"
              className={inputCls + " font-mono text-xs"}
            />
          </div>
          <div className="relative">
            <Gauge className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="number"
              value={form.sanctioned_load_kw}
              onChange={(e) => setField("sanctioned_load_kw", e.target.value)}
              placeholder="Sanctioned kW"
              data-testid="lead-sanctioned-load-input"
              className={inputCls + " font-mono text-xs"}
            />
          </div>
        </div>

        {/* Roof type */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Sun className="w-3.5 h-3.5" /> Roof Type</div>
          <div className="grid grid-cols-4 gap-2">
            {ROOF_TYPES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setField("roof_type", r.value)}
                data-testid={`roof-type-${r.value}`}
                className={"py-2 text-center text-[10px] font-semibold uppercase tracking-wider rounded-lg border transition-all " +
                  (form.roof_type === r.value ? "bg-[#1B5E20] text-white border-[#1B5E20]" : "bg-white text-slate-500 border-slate-200")}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Rooftop photo */}
        <input ref={roofInputRef} type="file" accept="image/*" capture="environment" onChange={onRoofSelected} className="hidden" data-testid="sc-roof-file-input" />
        <button
          type="button"
          onClick={() => roofInputRef.current?.click()}
          disabled={uploadingRoof}
          data-testid="sc-upload-roof-btn"
          className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3.5 text-left hover:border-emerald-300 transition-colors disabled:opacity-60 shadow-[0_2px_8px_rgba(15,23,42,0.04)]"
        >
          <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
            {uploadingRoof ? <Loader2 className="w-4.5 h-4.5 text-[#1B5E20] animate-spin" /> : <Camera className="w-4.5 h-4.5 text-[#1B5E20]" />}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">{roofPhotoPath ? "Rooftop photo added" : "Add rooftop photo (optional)"}</div>
            <div className="text-[11px] text-slate-500">{roofGeo ? "Geo-tagged ✓" : "Helps the survey team plan faster"}</div>
          </div>
          {roofPhotoPath && <CheckCircle2 className="w-4.5 h-4.5 text-[#1B5E20] flex-shrink-0" />}
        </button>

        <textarea
          value={form.notes}
          onChange={(e) => setField("notes", e.target.value)}
          rows={2}
          placeholder="Notes (optional)"
          data-testid="lead-notes-input"
          className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-[#1B5E20] focus:ring-2 focus:ring-emerald-600/15 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 transition-all duration-200 outline-none resize-none h-20"
        />

        <button
          type="submit"
          disabled={saving}
          data-testid="submit-lead-btn"
          className="w-full py-3.5 px-6 rounded-xl font-semibold text-white bg-[#1B5E20] hover:bg-[#15501A] active:scale-[0.99] shadow-sm transition-all duration-200 flex items-center justify-center gap-2 text-base disabled:opacity-60"
        >
          {saving ? "Sending…" : "Submit lead"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
